import React, { useCallback, useEffect, useState } from 'react';
import RollLogTab from './RollLogTab';
import { describeAttackResolution } from '../utils/rollLogFormat';
import { SPELL_DAMAGE_LOG_EVENT } from '../hooks/useSpellDamageFeedback';

const LOG_LIMIT = 100;

// The DM's Tools and Award XP buttons: the log tab sits on them.
const dmLogAnchor = () => [document.getElementById('dm-tools-menu'), document.getElementById('dm-award-xp-button')];

/**
 * The DM's roll log, like a player's: quick rolls (attack and damage buttons, checks), targeted monster attacks from
 * roll to HP, and finished spell saves. Rolls a targeted attack makes on the way are marked `log: false` and are
 * covered by its resolution entry instead. Newest first.
 */
export function useDmRollLog({ campaignId }) {
  const [entries, setEntries] = useState([]);
  const add = useCallback((items) => {
    const list = (Array.isArray(items) ? items : [items]).filter(Boolean);
    if (list.length) setEntries((previous) => [...list, { divider: true }, ...previous].slice(0, LOG_LIMIT));
  }, []);
  useEffect(() => {
    const rolled = ({ detail }) => {
      if (!detail || detail.log === false) return;
      add({ type: 'roll', source: detail.source || detail.rollLabel || 'Roll', total: detail.value, expression: detail.expression, actionLabel: detail.actionLabel, breakdown: detail.breakdown });
    };
    const saved = ({ detail }) => {
      if (!detail || detail.campaignId !== campaignId) return;
      add({ type: 'spell-save-resolution', source: detail.label, targetName: detail.targetName, success: detail.success, total: detail.finalDamage, damageType: detail.damageType,
        ...(Array.isArray(detail.logLines) ? { lines: detail.logLines } : {}) });
    };
    window.addEventListener('damage-roll', rolled);
    window.addEventListener(SPELL_DAMAGE_LOG_EVENT, saved);
    return () => { window.removeEventListener('damage-roll', rolled); window.removeEventListener(SPELL_DAMAGE_LOG_EVENT, saved); };
  }, [add, campaignId]);
  return { entries, add };
}

const Entry = ({ entry }) => {
  if (entry.type === 'attack-resolution') {
    const outcome = entry.outcome === 'critical-hit' ? 'Critical Hit' : entry.outcome === 'hit' ? 'Hit' : entry.grazeApplied ? 'Miss (Graze)' : entry.missDamage ? `Miss (${entry.missDamage.label})` : 'Miss';
    return (
      <div className="attack-resolution-log">
        <div><strong>{[entry.attackName, entry.targetName].filter(Boolean).join(' → ') || 'Attack'} · {outcome}</strong></div>
        <div>Attack: {entry.attackTotal}{Number.isFinite(entry.naturalRoll) ? ` (d20 ${entry.naturalRoll}${entry.naturalRoll === 20 ? ', natural 20' : ''})` : ''} vs AC {entry.targetArmorClass}{entry.cover ? ` (${entry.cover.label} +${entry.cover.bonus})` : ''}{entry.rollMode && entry.rollMode !== 'normal' ? ` · ${entry.rollMode}` : ''}</div>
        {describeAttackResolution(entry).map((line, index) => <div key={index}>{line}</div>)}
      </div>
    );
  }
  if (entry.type === 'spell-save-resolution') {
    return Array.isArray(entry.lines) && entry.lines.length > 0 ? (
      <div className="damage-log__save">
        <div><strong>{entry.lines[0]}</strong></div>
        {entry.lines.slice(1).map((line, index) => <div key={index}>{line}</div>)}
      </div>
    ) : <div><strong>{entry.source}</strong><div>{entry.targetName}: save {entry.success ? 'succeeded' : 'failed'}; {entry.total} {entry.damageType || 'spell'} damage</div></div>;
  }
  return (
    <div>
      <div><strong>{entry.source}</strong>{entry.total !== undefined && entry.total !== null ? ` - (${entry.total})` : ''}</div>
      {entry.expression && <div>{entry.actionLabel ? `${entry.actionLabel} - ` : ''}({entry.expression})</div>}
      {entry.breakdown && <div>{entry.breakdown}</div>}
    </div>
  );
};

export default function DmRollLog({ entries = [] }) {
  const [open, setOpen] = useState(false);
  return (
    <RollLogTab open={open} onOpenChange={setOpen} count={entries.filter((entry) => !entry.divider).length} anchorElements={dmLogAnchor}>
      <ul className="list-unstyled mb-0">
        {entries.map((entry, index) => (entry.divider
          ? <li key={index} className="roll-separator" />
          : <li key={index}><Entry entry={entry} /></li>))}
      </ul>
    </RollLogTab>
  );
}
